import Vue from 'vue';
import Router from 'vue-router';
import store from '@/plugins/vuex';
import AppsRoutes from '@/plugins/router/apps';
import JWT from '@/helpers/entities/classes/auth/Jwt';
import CryptoJS from 'crypto-js';
import { AES_SECRET_KEY } from '@/helpers/constants/app';
import { ConsoleLogger } from '@/helpers/entities/classes/logger/ConsoleLogger';
import DiyRoutes from './diy';
import { LandingPageRoutes } from './landing-pages';
const NotFound = () => import('@/views/NotFound.vue');
const Login = () => import('@/views/Login.vue');
const CognitoAuth = () => import('@/views/CognitoAuth.vue');
Vue.use(Router);
const routes = [
    {
        path: '/',
        redirect: { name: 'home' },
        component: () => import('@/views/Wrapper.vue'),
        children: [...LandingPageRoutes],
        beforeEnter: (to, from, next) => {
            next('/login/client');
        },
    },
    {
        path: '/login/:userType/:clevertapId?',
        props: true,
        name: 'login',
        component: Login,
        beforeEnter: (to, from, next) => {
            if (to.query.reauth === 'true') {
                store.commit('session/logout');
                store.commit('navigation_drawer/toggleDrawer', false);
                next();
            }
            else {
                next(store.getters['session/authenticated'] ? '/apps' : true);
            }
        },
    },
    {
        path: '/saml/auth/:authAction',
        props: true,
        name: 'cognito-saml-auth',
        component: CognitoAuth,
        beforeEnter: (to, from, next) => {
            if (to.query.reauth === 'true') {
                store.commit('session/logout');
                store.commit('navigation_drawer/toggleDrawer', false);
                next();
            }
            else {
                next(store.getters['session/authenticated'] ? '/apps' : true);
            }
        },
    },
    {
        path: '/store-manager/signup/:invitationToken',
        props: true,
        name: 'signup-as-store-manager',
        component: () => import('@/views/v2/store_manager/Signup.vue'),
    },
    {
        path: '/store-manager/signin',
        name: 'signin-as-store-manager',
        component: () => import('@/views/v2/store_manager/Signin.vue'),
    },
    {
        path: '/email-recipient-confirmation/:confirmationToken',
        props: true,
        name: 'email-recipient-confirmation',
        component: () => import('@/views/EmailRecipientConfirmation.vue'),
    },
    {
        path: '/support-email-recipient-confirmation/:confirmationToken',
        props: true,
        name: 'support-email-recipient-confirmation',
        component: () => import('@/views/SupportEmailRecipientConfirmation.vue'),
    },
    {
        path: '/admin-invitation/signup/:invitationToken',
        props: true,
        name: 'admin-invitation-signup',
        component: () => import('@/views/AdminInvitationSignUp.vue'),
    },
    {
        path: '/reset-password/:resetPasswordToken',
        props: true,
        name: 'reset-password',
        component: () => import('@/views/ResetPassword.vue'),
    },
    {
        path: '/unlock-account/:unlockToken',
        props: true,
        name: 'unlock-account',
        component: () => import('@/views/UnlockAccount.vue'),
    },
    {
        path: '/email-confirmation/:confirmationToken',
        props: true,
        name: 'email-confirmation',
        component: () => import('@/views/EmailConfirmation.vue'),
    },
    { path: '*', redirect: '/404' },
    {
        path: '/404',
        name: 'not-found',
        component: NotFound,
    },
    ...AppsRoutes,
    ...DiyRoutes,
];
const router = new Router({
    routes,
    mode: 'history',
    base: process.env.BASE_URL,
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    // @see https://router.vuejs.org/guide/advanced/scroll-behavior.html#scroll-behavior
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        return { x: 0, y: 0 };
    },
});
/**
 * Navigation Guards
 * https://router.vuejs.org/guide/advanced/navigation-guards.html
 */
router.beforeEach((to, from, next) => {
    /**
     * Log Route Navigation and Check if user navigate from Apps Dashboard
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.14.0}
     */
    const appDashboard = 'apps-dashboard';
    store.commit('session/fromAppsDashboard', from.name === appDashboard);
    store.commit('route/setFrom', from.name);
    store.commit('route/setTo', to.name);
    if (to.matched.some((record) => record.meta.authRequired)) {
        const logger = new ConsoleLogger();
        const home = { path: '/', params: { nextUrl: to.fullPath } };
        const token = store.getters['session/token'];
        if (token == null) {
            next(home);
        }
        else {
            const encodedToken = CryptoJS.enc.Base64.parse(token);
            const base64Token = encodedToken.toString(CryptoJS.enc.Utf8).split('\n').join('');
            const received = CryptoJS.enc.Base64.parse(base64Token);
            const salt = CryptoJS.enc.Hex.parse(received.toString().slice(0, 32));
            const iv = CryptoJS.enc.Hex.parse(received.toString().slice(32, 64));
            const cipher = CryptoJS.enc.Hex.parse(received.toString().slice(64));
            const key = CryptoJS.PBKDF2(AES_SECRET_KEY, salt, {
                keySize: 256 / 32,
                iterations: 100,
            });
            const decodedCipher = CryptoJS.AES.decrypt(cipher.toString(CryptoJS.enc.Base64), key, {
                iv,
            }).toString(CryptoJS.enc.Utf8);
            const jwt = new JWT(decodedCipher);
            if (jwt.isValidJwtCreated()) {
                // If there is a saved redirection, override the next route with that
                const redirectFrom = store.getters['route/redirectFrom'];
                const redirectTo = store.getters['route/redirectTo'];
                const redirectOverride = store.getters['route/redirectOverride'];
                if (from.name === redirectFrom && to.name === redirectTo) {
                    next({ name: redirectOverride });
                }
                else {
                    next();
                }
            }
            else {
                store.commit('session/logout');
                store.commit('navigation_drawer/toggleDrawer', false);
                logger.error('Expired access token');
                next(home);
            }
        }
    }
    else {
        next();
    }
});
router.onError((error) => {
    if (/loading chunk [a-zA-Z0-9-]* failed./i.test(error.message)) {
        window.location.reload();
    }
});
export default router;
